import { useMemo, useState, useCallback } from 'react';
import { useAIChat } from './useAIChat';
import { useUser } from './queries/useAuth';
import { useChatMessages, useAddChatMessage } from './queries/useChats';
import { FormattedChatMessage } from './queries/chatTypes';
import { ContextMessage, useContextManager } from './useContextManager';
import { logger } from '../utils/logger';
import { isAbortError } from '../utils/errorGuards';
import { SYSTEM_PROMPT } from '../constants/ai/prompts';
import { useTopic } from './queries/useTopics';
import { useProject } from './queries/useProjects';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateQuery } from '../utils/queryInvalidationUtils';

/**
 * Interface for chat hook return value
 */
export interface UseChatReturn {
  /** Whether the AI is currently generating a response */
  isTyping: boolean;
  /** Whether a silent retry is in progress after an error */
  isSilentlyRetrying: boolean;
  /** The current retry attempt (0-based index) */
  retryAttempt: number;
  /** Function to generate an AI response to a user message */
  generateAIResponse: (userMessage: string, model?: string) => Promise<void>;
  /** Function to cancel an in-progress generation */
  cancelGeneration: () => void;
  /** Array of chat messages for the current topic */
  messages: FormattedChatMessage[];
  /** Whether messages are currently loading */
  isLoading: boolean;
  /** Whether there was an error loading messages */
  isError: boolean;
}

/**
 * Hook for chat functionality with integrated retry logic
 * Uses React Query for efficient data fetching and caching
 *
 * @param topicId - The ID of the current chat topic or null if no topic is selected
 * @returns Chat functionality with retry support and message data
 */
export function useChat(topicId: string | null): UseChatReturn {
  const { data: user } = useUser();
  const queryClient = useQueryClient();

  // Get AI chat functionality with retry support
  const { isTyping, isSilentlyRetrying, retryAttempt, generateResponse, cancelRequest } =
    useAIChat();

  // Flag to track if we're currently generating a response
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  // Use React Query hooks to fetch data
  const { data: topicData } = useTopic(topicId);
  const { data: projectData } = useProject(topicData?.projectId || null);
  const { data: chatMessages = [], isLoading, isError } = useChatMessages(topicId);

  // Get mutation for adding messages
  const addChatMessage = useAddChatMessage();

  /**
   * Filter messages to only include those for this topic
   * This is already done by the useChatMessages hook, but we convert to ContextMessage format
   */
  const contextMessages = useMemo((): ContextMessage[] => {
    if (!topicId) return [];

    return chatMessages.map(msg => ({
      content: msg.content,
      role: msg.role as 'user' | 'assistant',
    }));
  }, [chatMessages, topicId]);

  // Initialize context manager with chat messages
  const contextManager = useContextManager({
    projectContext: projectData?.context,
    topicContext: topicData?.description,
    projectId: projectData?.id || '',
    topicId: topicId || '',
    messages: contextMessages,
  });

  /**
   * Generate an AI response to a user message with automatic retry
   * Memoized with useCallback to prevent unnecessary recreations
   *
   * @param userMessage - The message content from the user
   * @param model - Optional model ID to use for this request
   */
  const handleAIResponse = useCallback(
    async (userMessage: string, model?: string): Promise<void> => {
      if (!topicId || !user) return;

      // Prevent multiple concurrent generations
      if (isGenerating) {
        logger.ai('Already generating a response, ignoring new request');
        return;
      }

      setIsGenerating(true);

      try {
        // Get all context levels
        const { baseContext, conversationSummary, recentMessages } =
          contextManager.getCombinedContext();

        // Add user message immediately
        if (!user) {
          throw new Error('User not authenticated');
        }

        await addChatMessage.mutateAsync({
          topicId,
          content: userMessage,
          role: 'user',
          userId: user.id,
        });

        // Build messages array
        const messages = [
          // Recent messages for conversation flow
          ...recentMessages,

          // New user message
          {
            role: 'user',
            content: userMessage,
          },
        ];

        // Create a special system prompt with all context
        const enhancedSystemPrompt = [
          SYSTEM_PROMPT,
          baseContext.project ? `Project Context: ${baseContext.project}` : '',
          baseContext.topic ? `Topic Context: ${baseContext.topic}` : '',
          conversationSummary ? conversationSummary.content : '',
          recentMessages.length > 0
            ? 'IMPORTANT: The following messages are from an ongoing conversation. Consider the entire conversation history when responding.'
            : '',
        ]
          .filter(Boolean)
          .join('\n\n');

        // Log the request
        logger.ai('Generating chat response with context', {
          topicId,
          topicName: topicData?.name,
          messageCount: messages.length,
          project: projectData?.name,
          model: model || 'default',
          hasProjectContext: !!baseContext.project,
          hasTopicContext: !!baseContext.topic,
          hasConversationSummary: !!conversationSummary,
        });

        // Generate AI response using our enhanced hook
        const aiResponse = await generateResponse({
          messages,
          model: model,
          structuredResponse: true,
          systemPrompt: enhancedSystemPrompt,
        });

        // Handle the response
        let messageContent: string;
        let messageSummary: string | null = null;

        if (typeof aiResponse === 'string') {
          // Handle string response (fallback case)
          messageContent = aiResponse;
        } else {
          // Handle structured response
          messageContent = aiResponse.content;
          messageSummary = aiResponse.summary;
        }

        // Add AI message to topic with summary if available
        await addChatMessage.mutateAsync({
          topicId,
          content: messageContent,
          role: 'assistant',
          summary: messageSummary,
        });

        // Invalidate the topic to update last used timestamp
        invalidateQuery(queryClient, ['topics', 'detail', topicId], {
          refetchType: 'active',
          context: { operation: 'updateLastUsedAt', topicId },
        });
      } catch (error) {
        logger.error('Error in handleAIResponse:', error);

        // Don't show error message for aborted requests
        if (isAbortError(error)) {
          return;
        }

        // Add the error message to the chat
        await addChatMessage.mutateAsync({
          topicId,
          content: navigator.onLine
            ? error instanceof Error
              ? error.message
              : 'I apologize, but I encountered an error while processing your request. Please try again.'
            : 'I apologize, but I cannot generate a response while you are offline. Please check your internet connection and try again.',
          role: 'assistant',
          metadata: {
            isError: true,
            errorType: error instanceof Error ? error.name : 'UnknownError',
            retryAttempts: retryAttempt,
          },
        });
      } finally {
        setIsGenerating(false);
      }
    },
    [
      topicId,
      user,
      isGenerating,
      setIsGenerating,
      contextManager,
      addChatMessage,
      generateResponse,
      topicData,
      projectData,
      retryAttempt,
      queryClient,
    ]
  );

  // Return chat functionality
  return {
    isTyping: isTyping || isGenerating,
    isSilentlyRetrying,
    retryAttempt,
    generateAIResponse: handleAIResponse,
    cancelGeneration: cancelRequest,
    messages: chatMessages,
    isLoading,
    isError,
  };
}
